import React, { useState } from 'react';
import { Send, RotateCcw, Lightbulb } from 'lucide-react';
import { APP_CONFIG, REPORT_CONFIG } from '../config';
import { buildReportRequestPayload } from '../utils/generatorConfig';
import { t } from '../utils/i18n';
import LoadingSpinner from './LoadingSpinner';

const CompletionForm = ({ onSubmit, isLoading, onClear }) => {
  const initialValues = Object.fromEntries(
    REPORT_CONFIG.input.fields.map((field) => [field.key, field.default || ''])
  );
  const [values, setValues] = useState(initialValues);

  const updateField = (key, value) => {
    setValues((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = buildReportRequestPayload(values, REPORT_CONFIG.input.fields);
    if (payload.displayPrompt && !isLoading) {
      onSubmit(payload);
    }
  };

  const handleClear = () => {
    setValues(initialValues);
    if (onClear) onClear();
  };

  const handleExampleClick = (example) => {
    const primaryField =
      REPORT_CONFIG.input.fields.find((field) => field.type === 'long_text') ||
      REPORT_CONFIG.input.fields.find((field) => field.required) ||
      REPORT_CONFIG.input.fields[0];
    if (primaryField) {
      updateField(primaryField.key, example);
    }
  };

  const primaryField =
    REPORT_CONFIG.input.fields.find((field) => field.type === 'long_text') ||
    REPORT_CONFIG.input.fields.find((field) => field.required) ||
    REPORT_CONFIG.input.fields[0];
  const primaryValue = String(values[primaryField?.key] || '');
  const characterCount = primaryValue.length;
  const maxLength = primaryField?.maxLength || APP_CONFIG.UI_CONFIG.MAX_INPUT_LENGTH;
  const isOverLimit = characterCount > maxLength;
  const canSubmit = REPORT_CONFIG.input.fields
    .filter((field) => field.required)
    .every((field) => String(values[field.key] || '').trim());

  const renderField = (field) => {
    if (field.type === 'hidden') {
      return null;
    }
    const commonClasses = `w-full px-4 py-3 border-2 rounded-xl focus:outline-none transition-all duration-200 ${
      isOverLimit && field.key === primaryField?.key
        ? 'border-red-400 focus:border-red-500 focus:ring-4 focus:ring-red-100'
        : 'border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100'
    } placeholder:text-gray-400 text-gray-800 bg-gray-50 focus:bg-white`;

    return (
      <label key={field.key} className="block space-y-2">
        <span className="flex items-center gap-2 text-sm font-semibold text-gray-800">
          {field.label}
          {field.required && <span className="text-red-500">*</span>}
        </span>
        {field.type === 'long_text' ? (
          <textarea
            value={values[field.key] || ''}
            onChange={(e) => updateField(field.key, e.target.value)}
            placeholder={field.placeholder}
            rows={6}
            className={`${commonClasses} resize-none`}
            disabled={isLoading}
          />
        ) : field.type === 'select' ? (
          <select
            value={values[field.key] || ''}
            onChange={(e) => updateField(field.key, e.target.value)}
            className={commonClasses}
            disabled={isLoading}
          >
            <option value="">Select...</option>
            {field.options.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        ) : field.type === 'radio' ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {field.options.map((option) => (
              <button
                type="button"
                key={option.value}
                onClick={() => updateField(field.key, option.value)}
                disabled={isLoading}
                className={`px-4 py-3 rounded-xl border text-left transition-all ${
                  values[field.key] === option.value
                    ? 'border-blue-500 bg-blue-50 text-blue-800'
                    : 'border-gray-200 bg-gray-50 text-gray-700 hover:bg-white'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        ) : (
          <input
            type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
            value={values[field.key] || ''}
            onChange={(e) => updateField(field.key, e.target.value)}
            placeholder={field.placeholder}
            className={commonClasses}
            disabled={isLoading}
          />
        )}
      </label>
    );
  };

  return (
    <div className="w-full max-w-4xl mx-auto">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 gap-5">
          {REPORT_CONFIG.input.fields.map(renderField)}

          {/* 字符计数 */}
          {APP_CONFIG.UI_CONFIG.SHOW_CHARACTER_COUNT && primaryField && (
            <div className={`justify-self-end text-xs font-medium px-2 py-1 rounded-md ${
              isOverLimit
                ? 'text-red-600 bg-red-50'
                : characterCount > maxLength * 0.8
                  ? 'text-yellow-600 bg-yellow-50'
                  : 'text-gray-500 bg-white shadow-sm'
            }`}>
              {characterCount}/{maxLength}
            </div>
          )}
        </div>

        {/* 错误提示 */}
        {isOverLimit && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-700 text-sm font-medium">
              {t('promptTooLong', { maxLength })}
            </p>
          </div>
        )}

        {/* 按钮区域 */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            type="submit"
            disabled={isLoading || !canSubmit || isOverLimit}
            className="flex-1 flex items-center justify-center gap-3 px-6 py-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl hover:from-blue-600 hover:to-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl font-medium"
          >
            {isLoading ? (
              <>
                <LoadingSpinner size="sm" />
                <span>{t('generating')}</span>
              </>
            ) : (
              <>
                <Send className="w-5 h-5" />
                <span>{REPORT_CONFIG.input.submitLabel}</span>
              </>
            )}
          </button>

          {APP_CONFIG.UI_CONFIG.SHOW_CLEAR_BUTTON && (
            <button
              type="button"
              onClick={handleClear}
              disabled={isLoading}
              className="px-6 py-4 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 focus:outline-none focus:ring-4 focus:ring-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center gap-3 font-medium border border-gray-200 hover:border-gray-300"
            >
              <RotateCcw className="w-5 h-5" />
              <span>{t('clear')}</span>
            </button>
          )}
        </div>
      </form>

      {/* 示例提示词 */}
      {APP_CONFIG.SHOW_EXAMPLES && (REPORT_CONFIG.input.examples.length > 0 || APP_CONFIG.EXAMPLE_PROMPTS.length > 0) && (
        <div className="mt-10">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center">
              <Lightbulb className="w-5 h-5 text-yellow-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-800">{t('tryExamples')}</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {(REPORT_CONFIG.input.examples.length > 0 ? REPORT_CONFIG.input.examples : APP_CONFIG.EXAMPLE_PROMPTS).map((example, index) => (
              <button
                key={index}
                onClick={() => handleExampleClick(example)}
                disabled={isLoading}
                className="group p-4 text-left bg-gradient-to-br from-gray-50 to-gray-100 hover:from-blue-50 hover:to-blue-100 rounded-xl border border-gray-200 hover:border-blue-200 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md"
              >
                <p className="text-sm text-gray-700 group-hover:text-blue-800 transition-colors leading-relaxed">
                  {example}
                </p>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CompletionForm;
